/**
 * RoleCriteriaSummary.jsx — Slice W2 (Workspace Consolidation Pass)
 *
 * READ-ONLY surface that surfaces the role's structured definition
 * (required / preferred criteria, dealbreakers, dimension weighting)
 * on the role detail page. Sits above the existing role-definition
 * card so operators see what the LLM evaluator is comparing
 * candidates against without needing to open the edit dialog.
 *
 * Consumes role.structured_definition_json only. No endpoint.
 * No edits. No write paths.
 *
 * Colour doctrine:
 *   Emerald — required criteria (must-haves)
 *   Slate   — preferred criteria (nice-to-haves)
 *   Rose    — dealbreakers
 *   Blue    — weighting bars
 */
import { Card, CardContent } from '@/components/ui/card';
import { ShieldAlert, CheckCircle2, Sparkles, Scale } from 'lucide-react';


const DIMENSION_DEFAULTS = {
  seniority:         25,
  domain_alignment:  30,
  evidence_strength: 25,
  location:          10,
  risk:              10,
};

const DIMENSION_LABELS = {
  seniority:         'Seniority',
  domain_alignment:  'Domain',
  evidence_strength: 'Evidence',
  location:          'Location',
  risk:              'Risk',
};


function readArray(sdef, primaryKey, legacyKey) {
  const v = sdef?.[primaryKey] ?? sdef?.[legacyKey];
  return Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x.trim()).slice(0, 25) : [];
}


function readDealbreakers(sdef) {
  const v = sdef?.dealbreakers;
  if (!v) return [];
  if (Array.isArray(v)) {
    return v
      .filter((k) => typeof k === 'string' && k.trim())
      .map((k) => ({ key: k, value: '' }));
  }
  if (typeof v === 'object') {
    return Object.entries(v)
      .filter(([k]) => typeof k === 'string' && k.trim())
      .map(([k, val]) => ({ key: k, value: typeof val === 'string' ? val : '' }));
  }
  return [];
}


function readWeights(sdef) {
  const raw = sdef?.weighted_dimensions;
  if (!raw || typeof raw !== 'object') {
    return { weights: DIMENSION_DEFAULTS, isCustom: false };
  }
  const out = { ...DIMENSION_DEFAULTS };
  let touched = false;
  for (const k of Object.keys(DIMENSION_DEFAULTS)) {
    const n = Number(raw[k]);
    if (Number.isFinite(n) && n >= 0 && n <= 100) {
      out[k] = Math.round(n);
      if (Math.round(n) !== DIMENSION_DEFAULTS[k]) touched = true;
    }
  }
  return { weights: out, isCustom: touched };
}


export default function RoleCriteriaSummary({ role }) {
  if (!role) return null;
  const sdef = role.structured_definition_json || {};

  const required = readArray(sdef, 'required_criteria', 'must_have');
  const preferred = readArray(sdef, 'preferred_criteria', 'nice_to_have');
  const dealbreakers = readDealbreakers(sdef);
  const { weights, isCustom } = readWeights(sdef);

  const isEmpty =
    required.length === 0 &&
    preferred.length === 0 &&
    dealbreakers.length === 0;

  return (
    <Card
      className="border-ndor-edge bg-ndor-surface"
      data-testid="role-criteria-summary"
    >
      <CardContent className="p-5 space-y-5">
        <header className="space-y-1">
          <h3 className="text-sm font-semibold text-ndor-ink tracking-wide">
            What we evaluate against
          </h3>
          <p className="text-[10px] uppercase tracking-wider text-slate-300">
            Read-only · role definition
          </p>
        </header>

        {/* Required criteria */}
        <section className="space-y-2.5" data-testid="role-criteria-required">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-300 font-medium">
            <CheckCircle2 className="h-4 w-4" />
            Required ({required.length})
          </div>
          {required.length === 0 ? (
            <p className="text-sm text-slate-300 leading-relaxed">
              No required criteria defined. Add them in the role editor so evaluations have firm anchors.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {required.map((c, i) => (
                <span
                  key={`req-${i}`}
                  className="text-xs px-2.5 py-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Preferred criteria */}
        <section className="space-y-2.5" data-testid="role-criteria-preferred">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-200 font-medium">
            <Sparkles className="h-4 w-4" />
            Preferred ({preferred.length})
          </div>
          {preferred.length === 0 ? (
            <p className="text-sm text-slate-300">No preferred criteria.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {preferred.map((c, i) => (
                <span
                  key={`pref-${i}`}
                  className="text-xs px-2.5 py-1 rounded-md border border-slate-500/40 bg-slate-500/10 text-slate-200"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Dealbreakers */}
        <section className="space-y-2.5" data-testid="role-criteria-dealbreakers">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-rose-300 font-medium">
            <ShieldAlert className="h-4 w-4" />
            Dealbreakers ({dealbreakers.length})
          </div>
          {dealbreakers.length === 0 ? (
            <p className="text-sm text-slate-300 leading-relaxed">
              No dealbreakers configured. The four supported keys are timezone, clearance, work_authorization, language.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {dealbreakers.map(({ key, value }) => (
                <span
                  key={`db-${key}`}
                  className="text-xs px-2.5 py-1 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200"
                >
                  {key}{value ? <span className="text-rose-300/90"> · {value}</span> : null}
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Weighting summary */}
        <section className="space-y-3" data-testid="role-criteria-weighting">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-blue-300 font-medium">
              <Scale className="h-4 w-4" />
              Dimension weighting
            </div>
            <span
              className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded border ${
                isCustom
                  ? 'border-blue-500/40 bg-blue-500/10 text-blue-200'
                  : 'border-slate-500/40 bg-slate-500/10 text-slate-200'
              }`}
              data-testid="role-criteria-weighting-source"
              data-custom={isCustom ? 'true' : 'false'}
            >
              {isCustom ? 'Custom for this role' : 'NDOR default'}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-2.5">
            {Object.entries(weights).map(([k, v]) => (
              <div key={k} className="space-y-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs text-slate-200">{DIMENSION_LABELS[k]}</span>
                  <span className="text-xs font-semibold text-blue-200">{v}</span>
                </div>
                <div className="h-1.5 rounded bg-ndor-bg/60 overflow-hidden">
                  <div className="h-full bg-blue-500/70" style={{ width: `${v}%` }} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-300 leading-relaxed pt-1">
            Weights sum to 100 and govern the relative influence of each dimension on overall fit. Editing weights is gated behind a future P2 slice — this view is read-only.
          </p>
        </section>

        {isEmpty && (
          <p
            className="text-sm text-slate-300 leading-relaxed border-t border-ndor-edge pt-4"
            data-testid="role-criteria-empty-hint"
          >
            This role has minimal criteria defined. Evaluations will still run, but verdicts are most defensible when required criteria, preferred criteria, and dealbreakers are explicit.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
